import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * <b>Voltar para de onde se veio, com os filtros que estavam aplicados.</b>
 *
 * <p>🔴 <b>O problema</b> (PO, 2026-09-16). O `backTo` das páginas de detalhe é
 * um caminho FIXO — `/schools/:id/private-classes` —, então o chevron e o
 * breadcrumb devolvem a lista no estado padrão. Quem tinha filtrado por
 * "encerradas" e abriu uma aula voltava para a lista de ativas. Só a seta do
 * NAVEGADOR preservava, porque ela anda no histórico e não refaz a rota.
 *
 * <p>✅ <b>O idioma do React Router</b>, não invenção nossa: a origem passa
 * `state: { from }` no `navigate`, o destino lê. É o mesmo par que os docs usam
 * no exemplo de login/redirect, e o `location.state` já é vocabulário desta casa
 * — `Quizzes`, `QuizDetail` e `SaveUser` o usam para toast e para carregar item.
 * O que faltava era aplicá-lo a "de onde eu vim".
 *
 * <p>⚠️ <b>Guarda STRING, não o `location`.</b> O objeto traz `key` e o próprio
 * `state` — e como a origem já pode ter um `from`, guardar o objeto aninharia
 * `from.state.from.state…` a cada nível de navegação, crescendo o histórico do
 * browser sem teto. `pathname + search` é tudo que o retorno precisa.
 *
 * <p>⚠️ <b>Não substitui a URL como fonte.</b> Os filtros continuam na query da
 * listagem (é ela que o `from` carrega), então F5 e link compartilhado seguem
 * funcionando. Este par conserta o caminho que faltava: o de dentro do portal.
 *
 * <p>⚠️ <b>NÃO é o `originUrl` do `QuestionDetail`</b>, e os dois convivem de
 * propósito. Aquele responde <i>de qual ENTIDADE eu vim</i> — acervo, lista ou
 * simulado — lendo `?from=quiz&fromId=…` da URL, validando contra uma lista
 * fechada e remontando a URL canônica da origem (inclusive o nome dela no
 * breadcrumb). Este responde <i>de qual TELA e em que estado</i>, e devolve o
 * caminho literal. Unificá-los faria o primeiro perder a validação que ele tem
 * por ser entrada de fora.
 */

/** O caminho atual como string — o que o `from` guarda. */
const pathOf = (location) => location.pathname + (location.search || '');

/**
 * Na ORIGEM (a listagem): navega guardando de onde veio.
 *
 * <p>Troca direta pelo `useNavigate` — mesma assinatura, e o `state` que o
 * chamador passar é preservado.
 */
export function useNavigateFrom() {
	const navigate = useNavigate();
	const location = useLocation();
	return useCallback((to, options) => navigate(to, {
		...options,
		state: { ...(options && options.state), from: pathOf(location) },
	}), [navigate, location]);
}

/**
 * No DESTINO (a página de detalhe): o caminho de volta.
 *
 * @param {string} fallback usado quando se chegou por link direto, F5 ou
 *                          qualquer rota que não passou pelo `useNavigateFrom` —
 *                          e é por isso que ele continua obrigatório: a página
 *                          de detalhe tem que saber voltar sozinha.
 */
export function useReturnTo(fallback) {
	const location = useLocation();
	const from = location.state && location.state.from;
	/* ⚠️ Só caminho INTERNO. O `state` não vem de link, mas vem do histórico do
	   browser e é manipulável pelas devtools — e uma string como
	   `//evil.com/x` seria tratada como URL de protocolo relativo. A mesma
	   desconfiança que o `originUrl` tem por ler da query. */
	const isInternalPath = typeof from === 'string'
		&& from.startsWith('/') && !from.startsWith('//');
	return isInternalPath ? from : fallback;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * O ESTADO DA LISTAGEM — a outra metade de "voltar para onde eu estava".
 *
 * 🔴 `useReturnTo` responde PARA ONDE se volta; este par responde EM QUE ESTADO
 * a lista estava. São perguntas diferentes e por isso são peças diferentes: um
 * detalhe pode ter origem sem recorte (link direto) e recorte sem origem (F5 na
 * própria lista).
 *
 * <p>Extraído em 2026-10-21 do caso já EXERCITADO (Console › Membros), depois
 * de a terceira cópia do mesmo gesto aparecer — e cada cópia tinha errado uma
 * sutileza diferente. O que a peça carrega é só o que é comum e escorrega:
 *
 *   1. CAMPO VAZIO NÃO ATRAVESSA. Espalhar `{filteredColumn: undefined}` na
 *      volta APAGA o default da lista — o spread não distingue "não tenho" de
 *      "quero apagar". Custou um teste vermelho para aparecer.
 *   2. ORDEM É CHAVE IRMÃ, nunca campo dentro do recorte `[PO DECIDIU 21/10]`.
 *      O recorte tem um segundo emprego (semear a barra de filtros ao
 *      remontar), e ordenação ali dentro faz "Limpar filtros" zerar a ordem. É
 *      a mesma fronteira que o `FilterUrlKeys` do gabarito já declara.
 *   3. DEVOLVE SÓ O QUE É SEU. O `state` carrega também o `from` desta peça e o
 *      `success` de toast de quem navegou — repassá-lo inteiro devolveria o que
 *      não é nosso, e aninharia origem dentro de origem.
 *   4. A FONTE DEPENDE DO TIPO DO CAMPO. Id vem do ECO do servidor (`page`), que
 *      é de onde o `PagedList` já tira a setinha da coluna. OBJETO (fonte,
 *      assunto — os `FilterObjectKeys`) só existe no APLICADO: a query leva só
 *      `{id}` e a URL os exclui, então o eco não sabe o nome. Quem monta o
 *      recorte decide a fonte de cada chave; a peça não adivinha.
 *
 * ⚠️ Não é configuração de campos, de propósito: cada listagem declara os seus
 * no próprio `mergeQueryParams` (medido: de 5 a 13 campos, sem interseção
 * estável). Peça que tentasse conhecê-los viraria configuração infinita.
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Só os campos que a tela TEM; `undefined` quando não sobra nada. Ver a regra 1.
 *
 * <p>🔴 <b>String VAZIA atravessa</b> (22/10). Ela não é ausência — é escolha:
 * em Listas e Simulados a opção "Situação: todas" É o valor `''`, e o default da
 * primeira carga é `'true'` (ativas). Descartando o vazio, escolher "todas",
 * abrir um item e voltar devolvia a lista filtrada por ativas — a decisão
 * explícita da pessoa virava o default, sem nada explicando.
 * <p>⚠️ `undefined`/`null` continuam fora, e é o que resolve o problema que
 * originou esta função: espalhar `{filteredColumn: undefined}` na volta APAGA o
 * default. "Não tenho" e "quero vazio" são respostas diferentes.
 */
const definedOnly = (o) => {
	const out = Object.fromEntries(
		Object.entries(o || {}).filter(([, v]) => v !== undefined && v !== null));
	return Object.keys(out).length ? out : undefined;
};

/**
 * Na LISTAGEM: o `state` que viaja até o detalhe.
 *
 * @param {object} filters recorte APLICADO — com os objetos, quando houver
 * @param {object} sort    `{ sortBy, order }`, normalmente do eco do servidor
 * @returns {object} `{ listFilters?, listSort? }` — chaves ausentes quando vazias
 */
export function listStateOf(filters, sort) {
	const listFilters = definedOnly(filters);
	const listSort = definedOnly(sort);
	return {
		...(listFilters ? { listFilters } : {}),
		...(listSort ? { listSort } : {}),
	};
}

/**
 * No DETALHE: o `state` que o chevron devolve — só as duas chaves, nunca o
 * `state` inteiro. Ver a regra 3.
 *
 * <p>🔴 <b>Mais o `backState` que a ORIGEM declarou</b> `[PO DECIDIU 23/10]`. O
 * `from` guarda só `pathname + search`, e há contexto de origem que não cabe num
 * caminho: a ABA, por exemplo, vive no `state` (ver `useTabState`). Sem este
 * canal, sair do "Em uso" de uma questão para a lista e voltar abria a questão
 * na PRIMEIRA aba — a pessoa voltava para a tela certa no lugar errado.
 *
 * <p>⚠️ Não fere a regra 3 (<i>devolve só o que é seu</i>): o que volta é
 * exatamente o que a origem marcou como SEU, e nada do `state` do destino
 * vaza junto. Quem não manda `backState` não muda de comportamento.
 */
export function useListStateBack() {
	const location = useLocation();
	const s = location.state || {};
	const back = { ...listStateOf(s.listFilters, s.listSort), ...(s.backState || {}) };
	return Object.keys(back).length ? back : undefined;
}

/**
 * De que ENTIDADE é o caminho de volta — a pergunta que o RÓTULO do chevron faz.
 *
 * <p>🔴 <b>O rótulo acompanha o destino</b> (22/10). Com o `from` vindo do "Em
 * uso" de outra tela, dizer "Turmas" ou "Listas de Exercícios" seria o gatilho
 * mentindo sobre para onde leva. A regra nasceu escrita à mão no
 * `SchoolClassDetail` e virou peça quando o segundo e o terceiro leitor
 * apareceram (`QuizDetail`, `ExamDetail`) — `same_rule_in_three_places`.
 *
 * <p>⚠️ Só o DETALHE conta: `/questions/` casa, `/questions` (a listagem) não.
 * Voltar para a listagem é o caso comum, e o rótulo genérico de cada tela já o
 * nomeia.
 * <p>⚠️ Cada tela mapeia o tipo para o SEU rótulo: os fallbacks divergem
 * (turma × aula particular, lista × simulado) e centralizar isso viraria
 * configuração. A peça responde "de quem é o caminho", não "o que escrever".
 *
 * @param {string|null|undefined} path caminho JÁ validado como interno
 * @returns {'quiz'|'exam'|'question'|null}
 */
export function returnKindOf(path) {
	const p = typeof path === 'string' ? path : '';
	if (p.includes('/quizzes/')) return 'quiz';
	if (p.includes('/exams/')) return 'exam';
	if (p.includes('/questions/')) return 'question';
	return null;
}

/**
 * Na VOLTA (a listagem remontando): o que a primeira carga deve levar.
 *
 * ⚠️ Devolve recorte e ordem ACHATADOS num objeto só, porque é assim que o
 * `mergeQueryParams` das listagens consome — a separação existe no transporte,
 * não no query, onde os dois são parâmetro igual.
 */
export function useRestoredListQuery() {
	const location = useLocation();
	const s = location.state || {};
	return { ...(s.listFilters || {}), ...(s.listSort || {}) };
}

/**
 * O `state` de ORIGEM para um `<Link>`.
 *
 * <p>🔴 O `useNavigateFrom` serve a AÇÕES (kebab, clique de linha, botão). Um
 * `<Link>` navega sozinho, sem passar por ele — e sem `from` o destino não sabe
 * para onde voltar: cai no fallback, que é a listagem do próprio destino. Foi o
 * que o PO mediu em 22/10 vindo do "Em uso" de uma lista para o plano da turma:
 * o chevron oferecia voltar para Turmas, uma tela em que ele nunca esteve.
 *
 * <p>⚠️ Mesmo contrato do `useNavigateFrom`: guarda `pathname + search`, string
 * e não objeto — ver o javadoc dele.
 */
export function useFromState() {
	const location = useLocation();
	return { from: pathOf(location) };
}
